// components/Contacts.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useCookies } from "react-cookie";
import LocationAutocomplete from "../CustomMui/LocationStreetAutocomplete"; // if needed for addresses

const Contacts = () => {
  const navigate = useNavigate();
  const [cookies] = useCookies(["auth_token"]);
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // State for contact form; when editing, the fields are prefilled.
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    major: "",
    contactType: "Other", // e.g., "Primary", "Business", etc.
    contactTypeId: 4,
    alumni: false,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);

  // Fetch contacts for the signed-in company on mount
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        setLoading(true);
        const token = cookies.auth_token;
        const response = await axios.get("/api/company/contacts", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success) {
          setContacts(response.data.contacts);
        } else {
          setError("Failed to load contacts");
        }
      } catch (err) {
        console.error("Error fetching contacts:", err);
        setError("Error loading contacts");
      } finally {
        setLoading(false);
      }
    };
    fetchContacts();
  }, [cookies.auth_token]);

  // Handle changes in the form fields
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setContactForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle form submission for add/update
  const handleFormSubmit = async (e) => {
    e.preventDefault();
  
    // Check name length
    if (contactForm.name.length > 100) {
      setError("Name cannot exceed 100 characters.");
      return;
    }
    // Check email length
    if (contactForm.email.length > 100) {
      setError("Email cannot exceed 100 characters.");
      return;
    }
    // Check major length
    if (contactForm.major.length > 100) {
      setError("Major cannot exceed 100 characters.");
      return;
    }
    // Check contact limit
    if (!isEditing && contacts.length >= 12) {
      setError("Maximum of 12 contacts reached. Cannot add more contacts.");
      return;
    }
    // Override contactTypeId if Primary
    if (contactForm.contactType === "Primary") {
      contactForm.contactTypeId = 1;
    }
  
    try {
      const token = cookies.auth_token;
      if (isEditing) {
        // Update existing contact
        const response = await axios.put(`/api/company/contacts/${editingContactId}`, contactForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success) {
          setSuccessMessage("Contact updated successfully!");
          setContacts((prev) =>
            prev.map((contact) =>
              contact.contactId === editingContactId ? response.data.contact : contact
            )
          );
          clearForm();
        } else {
          setError(response.data.message || "Failed to update contact");
        }
      } else {
        // Add new contact
        const response = await axios.post("/api/company/contacts", contactForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success) {
          setSuccessMessage("Contact added successfully!");
          setContacts((prev) => [...prev, response.data.contact]);
          clearForm();
        } else {
          setError(response.data.message || "Failed to add contact");
        }
      }
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Error submitting contact:", err);
      setError("Error submitting contact");
    }
  };

  // Clear the form and exit edit mode
  const clearForm = () => {
    setContactForm({
      name: "",
      email: "",
      major: "",
      contactType: "Other",
      contactTypeId: 4,
      alumni: false,
    });
    setIsEditing(false);
    setEditingContactId(null);
  };

  // Populate form fields for editing
  const contactTypeMapping = ["0","Primary","Business","Associate","Other"];
  const handleEdit = (contact) => {
    setContactForm({
      name: contact.name || "",
      email: contact.email || "",
      major: contact.major || "",
      contactType: contact.contactType || "",
      contactTypeId: `${contactTypeMapping.indexOf(contact.contactType)}` || "",
      alumni: contact.alumni || false,
    });
    setIsEditing(true);
    setEditingContactId(contact.contactId);
  };

  // Delete a contact from the company's contact list
  const handleDelete = async (contactId) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) return;
    try {
      const token = cookies.auth_token;
      const response = await axios.delete(`/api/company/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setSuccessMessage("Contact deleted successfully!");
        setContacts((prev) => prev.filter((contact) => contact.contactId !== contactId));
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(response.data.message || "Failed to delete contact");
      }
    } catch (err) {
      console.error("Error deleting contact:", err);
      setError("Error deleting contact");
    }
  };

  // Handle dismissing error or success messages
  const handleDismissMessage = () => {
    setError(null);
    setSuccessMessage("");
  };

  // Redirect to login if not authenticated
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl font-semibold">Loading contacts...</div>
      </div>
    );
  }

  return (
    <div className="Homepage flex flex-col justify-center h-full">
      <div className="login-container">
        {/* Main container with nice background */}
        <div className="Homepage-page flex flex-col w-full max-w-6xl py-4 mx-auto my-auto">
          {/* Title Box */}
          <div className="bg-white rounded-2xl shadow p-6 text-black mb-4">
            <h2 className="text-3xl normal-case no-underline">Add a Contact:</h2>
          </div>

          {/* Form Box for Adding/Editing Contacts */}
          <div className="bg-white rounded-2xl shadow-md p-6 text-black mb-4">
            <form onSubmit={handleFormSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Contact Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={contactForm.name}
                    onChange={handleInputChange}
                    placeholder="Enter Contact's Full Name"
                    maxLength={100}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={contactForm.email}
                    onChange={handleInputChange}
                    placeholder="Enter Contact's email"
                    maxLength={100}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                {/* Major */}
                <div>
                  <label htmlFor="major" className="block text-sm font-medium text-gray-700 mb-1">
                    Degree, Major, & Year
                  </label>
                  <input
                    type="text"
                    id="major"
                    name="major"
                    value={contactForm.major}
                    onChange={handleInputChange}
                    placeholder="Enter degree, major, & year here if applicable"
                    maxLength={100}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      !contactForm.alumni ? "bg-gray-100 cursor-not-allowed" : ""
                    }`}
                    disabled={!contactForm.alumni}
                    required={contactForm.alumni}
                  />
                </div>
                {/* Contact Type */}
                <div>
                  <label htmlFor="contactType" className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Type *
                  </label>
                  {/*isEditing && contactForm.contactType === "Primary" ? ( */}
                    <input
                      type="text"
                      id="contactType"
                      name="contactType"
                      value={isEditing && contactForm.contactType === "Primary" ? "Primary" : "Other"}
                      readOnly
                      className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700"
                    />
                  {/*) : (
                    <select
                      id="contactTypeId"
                      name="contactTypeId"
                      value={contactForm.contactTypeId || ""}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select Contact Type</option>
                      <option value="2">Business</option>
                      <option value="3">Associate</option>
                      <option value="4">Other</option>
                    </select>
                  )*/}
                </div>
                {/* Alumni Checkbox */}
                <div className="flex items-center">
                  <label htmlFor="alumni" className="block text-sm font-medium text-gray-700 mr-2">
                    Alumni
                  </label>
                  <input
                    type="checkbox"
                    id="alumni"
                    name="alumni"
                    checked={contactForm.alumni}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                </div>
              </div>

              <div className="flex flex-col items-center gap-4 mt-6">
                <button
                  type="submit"
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {isEditing ? "Save Contact" : "Add Contact"}
                </button>
                {isEditing && (
                  <button
                    type="button"
                    onClick={clearForm}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text- gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                    Cancel Edit
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Contacts List */}
          <div className="bg-white rounded-2xl shadow-md p-6 text-black mb-4 max-h-[40vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Your Contacts</h3>
            {contacts.length === 0 ? (
              <p>No contacts found.</p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="top-0 bg-white z-10 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="top-0 bg-white z-10 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="top-0 bg-white z-10 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Degree, Major, & Year
                    </th>
                    <th className="top-0 bg-white z-10 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact Type
                    </th>
                    <th className="top-0 bg-white z-10 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Alumni
                    </th>
                    <th className="sticky top-0 bg-white z-10 px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {contacts.map((contact) => (
                    <tr key={contact.contactId}>
                      <td className="px-6 py-4 whitespace-nowrap">{contact.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{contact.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{contact.major}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{contact.contactType}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {contact.alumni ? "Yes" : "No"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(contact)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          Edit
                        </button>
                        {contact.contactType !== "Primary" && (
                          <button
                            onClick={() => handleDelete(contact.contactId)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Display error or success messages as modal */}
          {(error || successMessage) && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full text-center">
                {error && (
                  <div className="mb-4">
                    <p className="text-xl font-semibold text-red-600">Error</p>
                    <p className="text-gray-700 mt-2">{error}</p>
                  </div>
                )}
                {successMessage && (
                  <div className="mb-4">
                    <p className="text-xl font-semibold text-green-600">Success</p>
                    <p className="text-gray-700 mt-2">{successMessage}</p>
                  </div>
                )}
                <button
                  onClick={handleDismissMessage}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Contacts;